import { useState } from "react";
import { useSearch } from "@tanstack/react-router";

import { useGrows } from "@/lib/api/grows";
import {
  useBulkDismissPendingActions,
  usePendingActionsAcrossGrows,
  usePendingActionsForGrow,
} from "@/lib/api/pending-actions";
import type { Grow, PendingAction } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorState } from "@/components/error-state";
import { UrgencyBadge } from "@/components/urgency-badge";
import { ActionGroups } from "@/components/pending-actions/action-groups";
import { NewActionForm } from "@/components/pending-actions/action-form";

// Pending Actions is the grower's inbox. It defaults to OPEN actions across
// ALL grows (same per-grow cache the dashboard reuses), grouped by source
// refill so related actions read as one sequence. The grow <Select> is a
// FILTER — picking a specific grow narrows to that grow (and unlocks the
// include-dismissed toggle, which is single-grow on the server). A `?grow=`
// search param pre-selects a grow when arriving from a grow card.

// Radix Select reserves the empty string for "no value / show placeholder",
// so the "all grows" option uses a sentinel.
const ALL_GROWS = "__all__";

export function PendingActionsPage() {
  const { grow: growParam } = useSearch({ from: "/pending-actions" });
  const grows = useGrows();
  const items = grows.data?.items ?? [];
  const active = items.filter((g) => !g.archived_at);
  const growsById = new Map(items.map((g) => [g.id, g]));

  // Filter defaults straight from the `?grow=` param so a deep-link from a grow
  // card pre-filters even on a cold load (the panel only needs the id; the grow
  // chip fills in once useGrows resolves). Falls back to all grows when no param
  // is present. Local state so the Select can change it without a navigation.
  const [filterGrow, setFilterGrow] = useState<string>(growParam ?? ALL_GROWS);
  const [showNew, setShowNew] = useState(false);

  const isAll = filterGrow === ALL_GROWS;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Pending actions
        </h1>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant={showNew ? "secondary" : "outline"}
            onClick={() => setShowNew((v) => !v)}
            aria-expanded={showNew}
          >
            {showNew ? "Close" : "New action"}
          </Button>
          <div className="min-w-[16rem]">
            <Select value={filterGrow} onValueChange={setFilterGrow}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All grows" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_GROWS}>All grows</SelectItem>
                {active.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.species} — {g.system_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {grows.error && <ErrorState error={grows.error} retry={grows.refetch} />}

      {showNew && (
        <NewActionForm
          grows={items}
          defaultGrowID={isAll ? undefined : filterGrow}
          onDone={() => setShowNew(false)}
        />
      )}

      {grows.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : active.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No grows yet — create one to see pending actions.
        </p>
      ) : isAll ? (
        <AllGrowsPanel grows={active} growsById={growsById} />
      ) : (
        <SingleGrowPanel growID={filterGrow} growsById={growsById} />
      )}
    </div>
  );
}

// AllGrowsPanel rolls up OPEN actions across every active grow (shared cache
// with the dashboard / grow cards) and renders them grouped by source refill.
function AllGrowsPanel({
  grows,
  growsById,
}: {
  grows: Grow[];
  growsById: Map<string, Grow>;
}) {
  const { items, isLoading } = usePendingActionsAcrossGrows(
    grows.map((g) => g.id),
  );
  const open = items.filter((a) => !a.dismissed_at);

  if (open.length === 0) {
    return isLoading ? (
      <p className="text-sm text-muted-foreground">Loading…</p>
    ) : (
      <p className="text-sm text-muted-foreground">No open actions. ✨</p>
    );
  }

  return <ActionGroups rows={open} growsById={growsById} />;
}

// SingleGrowPanel narrows to one grow and unlocks the include-dismissed toggle
// + bulk-dismiss-for-grow affordance (both server-side grow-scoped). Open rows
// reuse the grouped layout; dismissed rows render statically (nothing to act
// on) when the toggle is enabled.
function SingleGrowPanel({
  growID,
  growsById,
}: {
  growID: string;
  growsById: Map<string, Grow>;
}) {
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const q = usePendingActionsForGrow(growID, includeDismissed);
  const bulk = useBulkDismissPendingActions();

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;

  const rows = q.data?.items ?? [];
  const open = rows.filter((r) => !r.dismissed_at);
  const dismissed = rows.filter((r) => r.dismissed_at);

  function onBulkDismiss() {
    const reason =
      window.prompt("Reason for dismissing every open action?") ||
      "bulk dismiss";
    bulk.mutate({ grow_id: growID, reason });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={includeDismissed}
            onChange={(e) => setIncludeDismissed(e.target.checked)}
          />
          include dismissed
        </label>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBulkDismiss}
          disabled={bulk.isPending || open.length === 0}
        >
          {bulk.isPending ? "Dismissing…" : "Dismiss all open"}
        </Button>
      </div>

      {open.length === 0 && dismissed.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {includeDismissed
            ? "No actions recorded for this grow."
            : "No open actions. ✨"}
        </p>
      ) : (
        <ActionGroups rows={open} growsById={growsById} />
      )}

      {includeDismissed && dismissed.length > 0 && (
        <section className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Dismissed</p>
          <ul className="divide-y rounded-md border">
            {dismissed.map((row) => (
              <DismissedRow key={row.id} row={row} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// Dismissed rows are static — there's nothing to act on, so they don't get the
// inline action buttons / expand chrome that open rows do. The label reflects
// the recorded resolution ("completed" / "skipped" / "dismissed"), falling
// back to "dismissed" for rows from before resolutions were tracked.
function DismissedRow({ row }: { row: PendingAction }) {
  const resolution = row.resolution ?? "dismissed";
  const cls =
    resolution === "completed"
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-muted-foreground";
  return (
    <li className="px-4 py-3">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <UrgencyBadge urgency={row.urgency} />
          <span className="font-medium">{row.title}</span>
          <span className={"text-xs " + cls}>{resolution}</span>
        </div>
        {row.rationale && (
          <p className="line-clamp-1 text-sm text-muted-foreground">
            {row.rationale}
          </p>
        )}
      </div>
    </li>
  );
}
